import React, { FC } from 'react';
import { getPdpUrl } from 'helpers/utils/pdpUrlHelper';
import { ITEM_CODE } from 'helpers/constants/attributes';

interface Props {
  product: any; // mapProduct() result
  locale: string;
}

/**
 * Lightweight, hook-free, NON-interactive product tile for the OFF-SCREEN (display:none) tiles in the
 * SSR grid. It keeps the product crawlable — a real <a href> to the PDP plus name, image and code
 * are in the DOM for SEO — but costs ~nothing to hydrate (no hooks, no event handlers). The full
 * interactive <ProductTile> (swatch dropdown, tracking, etc.) is only mounted for the VISIBLE batch;
 * when a tile scrolls into range the grid swaps this static tile for the interactive one. This is
 * what actually cuts hydration from ~all tiles down to the visible ~12, and it also shrinks the SSR
 * HTML (the off-screen tiles are tiny markup instead of full ProductTile output).
 *
 * These tiles are always rendered inside a `hidden` (display:none) wrapper, so their exact visual
 * styling never paints — the markup exists purely for crawlers + as a placeholder until interactive.
 */
const StaticProductTile: FC<Props> = ({ product, locale }) => {
  const selectedVariant =
    product?.selectedVariant ??
    product?.variants?.find((v: any) => v.id === product?.masterVariantID) ??
    product?.variants?.[0];

  const url = `/${locale}${
    product?._url || getPdpUrl(product?.slug, selectedVariant?.sku, selectedVariant?.attributes?.colour)
  }`;
  const name = product?.name || '';
  const code = selectedVariant?.attributes?.[ITEM_CODE] || product?.productcode || product?.variants?.[0]?.key || '';
  const image = selectedVariant?.images?.[0];
  const category = product?.categories?.[0]?.categoryId || '';

  return (
    <a href={url} className="pt-card">
      <div className="pt-media" style={{ paddingBottom: '100%' }}>
        {image && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={encodeURI(image.toString())}
            alt={name}
            loading="lazy"
            className="pt-media-img absolute inset-0 h-full w-full"
          />
        )}
      </div>
      <div className="pt-body">
        {category && <span className="pt-category">{category}</span>}
        <div className="pt-name-row">
          <p className="pt-name">{name}</p>
        </div>
        {code && (
          <div className="pt-code">
            Product code <span className="pt-code-val">{code}</span>
          </div>
        )}
      </div>
    </a>
  );
};

export default StaticProductTile;
